import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Filters } from '../FindToolbox/Filters';
import { useSettings } from '../context/SettingsContext/useSettings';
import { FindResultFilter, FindResultsState } from './types';
import {
  cleanFindFilter,
  EMPTY_FIND_FILTER_LIST_KEYS,
  FindFilterListKey,
} from '../FindToolbox/Filters/types';
import { FindPresetDialog, FindPresetDialogMode } from '../FindPresetDialog';
import { useCanUseFindPresets } from '../../hooks';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, IconButton } from '../design-system';

const paperStyles = {
  width: { xs: 320, sm: 400 },
  maxWidth: 'calc(100vw - 32px)',
  p: 2.5,
  mt: 0.75,
  border: '1px solid',
  borderColor: 'divider',
  backgroundImage: 'none',
};
const smallIconStyles = { width: 13, height: 13 };
const copyFilterIconStyles = { ...smallIconStyles, ml: '5px !important' };
const countBadgeStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  bgcolor: 'rgba(255, 255, 255, 0.25)',
  fontSize: 12,
  fontWeight: 700,
  ml: 0.75,
};

interface FindListFiltersProps {
  state: FindResultsState;
  enableSiteFilter?: boolean;
  enableGroupFilter?: boolean;
  showCopyFromMap?: boolean;
  presetOmittedKeys?: readonly FindFilterListKey[];
}

export function FindListFilters({
  state,
  enableSiteFilter = true,
  enableGroupFilter = true,
  showCopyFromMap = true,
  presetOmittedKeys = EMPTY_FIND_FILTER_LIST_KEYS,
}: FindListFiltersProps) {
  const intl = useIntl();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [presetMode, setPresetMode] = useState<FindPresetDialogMode | null>(
    null
  );
  const { filter, activeCount, setFilter, clearFilter } = state;
  const {
    store: { activeFindFilter },
  } = useSettings();
  const canUsePresets = useCanUseFindPresets();

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleCopyFromMap = () => {
    if (!activeFindFilter) {
      return;
    }

    const next = cleanFindFilter({
      sites: activeFindFilter.sites,
      eras: activeFindFilter.eras,
      findTypes: activeFindFilter.findTypes,
      materials: activeFindFilter.materials,
      groups: activeFindFilter.groups,
      tags: activeFindFilter.tags,
      hasImages: activeFindFilter.hasImages,
      hasAmcrId: activeFindFilter.hasAmcrId,
    });
    setFilter(next);
  };

  const handleChange = (nextFilter: FindResultFilter) => {
    setFilter(cleanFindFilter(nextFilter));
  };

  const hasMapFilter = !!(
    showCopyFromMap &&
    activeFindFilter &&
    (activeFindFilter.sites?.length ||
      activeFindFilter.eras?.length ||
      activeFindFilter.findTypes?.length ||
      activeFindFilter.materials?.length ||
      activeFindFilter.groups?.length ||
      activeFindFilter.tags?.length ||
      activeFindFilter.hasImages != null ||
      activeFindFilter.hasAmcrId != null)
  );

  const handleToggle = () => setOpen((previous) => !previous);
  const handleClose = () => setOpen(false);
  const handlePresetClose = () => setPresetMode(null);
  const handleOpenPreset = (mode: FindPresetDialogMode) => {
    setOpen(false);
    setPresetMode(mode);
  };
  const handleOpenLoadPreset = () => handleOpenPreset('load');
  const handleOpenSavePreset = () => handleOpenPreset('save');
  const handleLoadPreset = (loadedFilter: FindResultFilter | null) => {
    setFilter(loadedFilter);
  };

  return (
    <>
      <Box ref={anchorRef} display="inline-flex">
        <Button
          tone="neutral"
          onClick={handleToggle}
          startIcon={<FilterAltIcon />}
        >
          <FormattedMessage id="finds.filters.title" defaultMessage="Filters" />
          {activeCount > 0 && (
            <Box component="span" sx={countBadgeStyles}>
              {activeCount}
            </Box>
          )}
        </Button>
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={8} sx={paperStyles}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Typography fontWeight={600}>
                <FormattedMessage
                  id="finds.filters.title"
                  defaultMessage="Filters"
                />
              </Typography>
              <IconButton
                density="compact"
                aria-label={intl.formatMessage({
                  id: 'finds.filters.close',
                  defaultMessage: 'Close filters',
                })}
                onClick={handleClose}
                variant="ghost"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Filters
              filter={filter}
              onChange={handleChange}
              onRemove={clearFilter}
              enableSiteFilter={enableSiteFilter}
              enableGroupFilter={enableGroupFilter}
              enableImageFilter
              enableAmcrIdFilter
              enablePreset={false}
              showRemoveButton={false}
            />
            {activeCount > 0 && (
              <Stack alignItems="flex-start">
                <Button
                  startIcon={<FilterAltOffIcon />}
                  tone="danger"
                  onClick={clearFilter}
                >
                  {activeCount > 1
                    ? intl.formatMessage({
                        id: 'filter.removeMultiple',
                        defaultMessage: 'Remove filters',
                      })
                    : intl.formatMessage({
                        id: 'filter.remove',
                        defaultMessage: 'Remove filter',
                      })}
                </Button>
              </Stack>
            )}
            {canUsePresets && (
              <Stack direction="row" gap={1} pt={3}>
                <Button
                  size="small"
                  tone="neutral"
                  variant="outlined"
                  onClick={handleOpenLoadPreset}
                >
                  <FormattedMessage
                    id="filter.preset.load"
                    defaultMessage="Load preset"
                  />
                </Button>
                <Button
                  size="small"
                  tone="neutral"
                  variant="outlined"
                  onClick={handleOpenSavePreset}
                >
                  <FormattedMessage
                    id="filter.preset.save"
                    defaultMessage="Save preset"
                  />
                </Button>
              </Stack>
            )}
            {hasMapFilter && (
              <Stack direction="row" alignItems="center" pt={2.5}>
                <Chip
                  icon={<ContentCopyIcon sx={copyFilterIconStyles} />}
                  size="small"
                  label={intl.formatMessage({
                    id: 'finds.filters.copyFromMap',
                    defaultMessage: 'Copy from map',
                  })}
                  variant="outlined"
                  onClick={handleCopyFromMap}
                />
              </Stack>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
      {canUsePresets && presetMode && (
        <FindPresetDialog
          mode={presetMode}
          open
          filter={filter}
          omittedKeys={presetOmittedKeys}
          onClose={handlePresetClose}
          onLoad={handleLoadPreset}
        />
      )}
    </>
  );
}
